import { useCallback, useContext } from "react";
import axios, { AxiosRequestConfig, HttpStatusCode } from "axios";

import AuthContext from "common/store/AuthContext";

import { generateErrorMsg, getServerUrl } from "utils/commonTools";
import { toJapaneseDate, createFileNameDate } from "utils/DateTools";
import { GetBasicResponse } from "common/types/responses/BasicResponse";
import { TasksByTaskType } from "../types/TasksByTaskType";
import { PrintTasksRequestInfo } from "../types/PrintTasksRequestInfo";
import { useGetPrint } from "./useGetPrint";

export const useTasksAndPrints = () => {
  const authCtxt = useContext(AuthContext);
  const { token } = authCtxt;

  const { initiateDownload } = useGetPrint();

  //--------------------------------------
  // 指定した期間内に実施された作業の一覧を取得する
  //--------------------------------------
  const getConductedTasks = useCallback(
    async (
      parkId: number,
      from: Date,
      to: Date
    ): Promise<{ succeeded: boolean; msg: string; data?: TasksByTaskType[] }> => {
      const config = {
        headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
      };

      try {
        const queryParam = `from=${toJapaneseDate(from)}&to=${toJapaneseDate(to)}`;
        const { data } = await axios.get<GetBasicResponse<TasksByTaskType[]>>(
          getServerUrl() + `workReport/report/${parkId}/tasksByPeriod?${queryParam}`,
          config
        );
        return { succeeded: true, msg: "", data: data.data };
      } catch (error) {
        var errMsg = generateErrorMsg(error);
        return { succeeded: false, msg: errMsg };
      }
    },
    [token]
  );

  const printReport = async (
    tasks: PrintTasksRequestInfo[],
    startDate: Date,
    endDate: Date,
    title: string
  ): Promise<{ succeeded: boolean; msg: string }> => {
    const config = {
      headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
      responseType: "blob",
      params: {
        from: toJapaneseDate(startDate),
        to: toJapaneseDate(endDate),
      },
    } as AxiosRequestConfig;

    const parkId = authCtxt.selectedPark?.parkId;
    const parkName = authCtxt.selectedPark?.parkName;

    try {
      const response = await axios.post(
        getServerUrl() + `print/taskAndPictures/${parkId}`,
        tasks,
        config
      );
      if (response.status === HttpStatusCode.NoContent) {
        return {
          succeeded: false,
          msg: "レポートを生成できませんでした（帳票が未登録です）",
        };
      }
      const url = window.URL.createObjectURL(response.data);

      const fileName = `${title}_${parkName}_${createFileNameDate(startDate)}_${createFileNameDate(
        endDate
      )}.xlsx`;
      initiateDownload(url, fileName);
      return { succeeded: true, msg: "" };
    } catch (error) {
      var errMsg = generateErrorMsg(error);
      return { succeeded: false, msg: errMsg };
    }
  };

  return { getConductedTasks, printReport };
};
